import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { BookmarkMinus } from 'lucide-react';
import EventFinderAPI from '../services/api';
import type {
  Event,
  EventGroupMapLocation,
  Pagination,
  RememberedEventEventsResponse,
} from '../types/api';
import { SEO } from '../components/SEO';
import EventsExplorer, { ExplorerControlButton } from '../components/events/EventsExplorer';
import { useRememberedEvents } from '../contexts/RememberedEventsContext';
import { useAnalytics } from '../hooks/useAnalytics';
import type { ExportFormat } from '../utils/eventExport';
import { spacing, typography, colors } from '../styles/theme';

const PAGE_SIZE = 20;
const FILTER_KEYS = ['days', 'from', 'to', 'format', 'rel', 'tod', 'vibe', 'hideEntity'];

const Breadcrumbs = styled.nav`
  margin-bottom: ${spacing[3]};
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.sm};

  a {
    color: ${colors.text.link};
    text-decoration: none;
  }
`;

const Subtitle = styled.span`
  display: block;
  max-width: 760px;
  line-height: ${typography.lineHeights.relaxed};
`;

function apiFilters(searchParams: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = searchParams.get(key);
    if (value) params.set(key, value);
  }
  return params;
}

function requestParams(
  filters: URLSearchParams,
  scope: 'upcoming' | 'recent' | 'all',
  page = 1,
): URLSearchParams {
  const params = new URLSearchParams(filters);
  params.set('scope', scope);
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));
  return params;
}

function appendUnique(current: Event[], next: Event[]): Event[] {
  const seen = new Set(current.map(event => event.eventId));
  return [...current, ...next.filter(event => !seen.has(event.eventId))];
}

function mapLocationEvents(location: EventGroupMapLocation): Event[] {
  return location.events.map(event => ({
    ...event,
    city: location.city || undefined,
    state: location.state || undefined,
    country: location.country || undefined,
    coordinates: location.coordinates,
    entities: [],
  }));
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export const MyEventsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const rememberedEvents = useRememberedEvents();
  const { trackEvent } = useAnalytics();
  const filters = useMemo(() => apiFilters(searchParams), [searchParams]);
  const filterKey = filters.toString();
  const activeFilterKey = useRef(filterKey);

  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [recentEvents, setRecentEvents] = useState<Event[]>([]);
  const [upcomingPagination, setUpcomingPagination] = useState<Pagination | null>(null);
  const [recentPagination, setRecentPagination] = useState<Pagination | null>(null);
  const [vibeOptions, setVibeOptions] = useState<string[]>([]);
  const [entityOptions, setEntityOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [mapEvents, setMapEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [isRemovingPast, setIsRemovingPast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (rememberedEvents.isLoading || !rememberedEvents.browserId) return;
    activeFilterKey.current = filterKey;
    const controller = new AbortController();
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setUpcomingEvents([]);
    setRecentEvents([]);
    setMapEvents([]);
    setUpcomingPagination(null);
    setRecentPagination(null);

    (async () => {
      try {
        const [upcoming, recent, map] = await Promise.all([
          EventFinderAPI.getRememberedEvents(
            rememberedEvents.browserId,
            requestParams(filters, 'upcoming'),
            controller.signal,
          ),
          EventFinderAPI.getRememberedEvents(
            rememberedEvents.browserId,
            requestParams(filters, 'recent'),
            controller.signal,
          ),
          EventFinderAPI.getRememberedEventMap(
            rememberedEvents.browserId,
            requestParams(filters, 'all'),
            controller.signal,
          ),
        ]);
        if (cancelled) return;
        setUpcomingEvents(upcoming.events);
        setRecentEvents(recent.events);
        setUpcomingPagination(upcoming.pagination);
        setRecentPagination(recent.pagination);
        setVibeOptions(Array.from(new Set([...upcoming.facets.vibes, ...recent.facets.vibes])).sort());
        const entities = new Map<number, string>();
        for (const facet of [...upcoming.facets.entities, ...recent.facets.entities]) {
          entities.set(facet.entityId, facet.displayName);
        }
        setEntityOptions(Array.from(entities, ([value, label]) => ({ value, label }))
          .sort((left, right) => left.label.localeCompare(right.label)));
        setMapEvents(map.locations.flatMap(mapLocationEvents));
      } catch (loadError) {
        if (cancelled || (loadError as Error).name === 'AbortError') return;
        setError((loadError as Error).message || 'My Events could not be loaded.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `filterKey` deliberately excludes view mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, reloadToken, rememberedEvents.browserId, rememberedEvents.isLoading, rememberedEvents.revision]);

  const loadMore = useCallback(async (scope: 'upcoming' | 'recent') => {
    const pagination = scope === 'upcoming' ? upcomingPagination : recentPagination;
    if (!pagination?.hasNextPage) return;
    const requestFilterKey = activeFilterKey.current;
    if (scope === 'upcoming') setLoadingUpcoming(true);
    else setLoadingRecent(true);
    try {
      const result: RememberedEventEventsResponse = await EventFinderAPI.getRememberedEvents(
        rememberedEvents.browserId,
        requestParams(apiFilters(new URLSearchParams(requestFilterKey)), scope, pagination.page + 1),
      );
      if (activeFilterKey.current !== requestFilterKey) return;
      if (scope === 'upcoming') {
        setUpcomingEvents(current => appendUnique(current, result.events));
        setUpcomingPagination(result.pagination);
      } else {
        setRecentEvents(current => appendUnique(current, result.events));
        setRecentPagination(result.pagination);
      }
      trackEvent({
        category: 'remembered_events',
        action: 'load_more',
        label: scope,
        value: result.events.length,
        custom_parameters: { action_location: 'event_list', scope },
      });
    } catch (loadError) {
      setError((loadError as Error).message || 'More remembered events could not be loaded.');
    } finally {
      if (scope === 'upcoming') setLoadingUpcoming(false);
      else setLoadingRecent(false);
    }
  }, [recentPagination, rememberedEvents.browserId, trackEvent, upcomingPagination]);

  const handleForgetPast = useCallback(async () => {
    setIsRemovingPast(true);
    try {
      const result = await rememberedEvents.forgetPast();
      trackEvent({
        category: 'remembered_events',
        action: 'forget_past',
        value: result.removed,
        custom_parameters: { action_location: 'my_events_toolbar', outcome: 'success' },
      });
    } catch (removeError) {
      setError((removeError as Error).message || 'Past events could not be removed.');
    } finally {
      setIsRemovingPast(false);
    }
  }, [rememberedEvents, trackEvent]);

  const handleExport = useCallback(async (format: ExportFormat) => {
    try {
      const blob = await EventFinderAPI.getRememberedEventsExport(
        rememberedEvents.browserId,
        format,
        filters,
      );
      const extension = format === 'markdown' ? 'md' : format === 'text' ? 'txt' : 'csv';
      downloadBlob(blob, `my-events.${extension}`);
    } catch (exportError) {
      setError((exportError as Error).message || 'The My Events export could not be created.');
    }
  }, [filters, rememberedEvents.browserId]);

  const visibleIds = rememberedEvents.eventIds;
  const allEvents = [...upcomingEvents, ...recentEvents]
    .filter(event => visibleIds.has(event.eventId));
  const upcomingTotal = upcomingPagination?.totalItems || 0;
  const recentTotal = recentPagination?.totalItems || 0;
  const total = upcomingTotal + recentTotal;

  return (
    <>
      <SEO
        title="My Events"
        description="Events remembered in this browser."
        noIndex
      />
      <EventsExplorer
        title="My Events"
        subtitle={(
          <Subtitle>
            Events remembered in this browser. Saves expire after 12 months without a visit,
            and events remain available for seven days after they end.
          </Subtitle>
        )}
        breadcrumbs={(
          <Breadcrumbs aria-label="Breadcrumb">
            <a href="/">Home</a>
            {' / '}
            <span aria-current="page">My Events</span>
          </Breadcrumbs>
        )}
        events={allEvents}
        mapEvents={mapEvents.filter(event => visibleIds.has(event.eventId))}
        isLoading={isLoading || rememberedEvents.isLoading}
        error={error || rememberedEvents.error}
        onRetry={() => setReloadToken(token => token + 1)}
        totalAvailable={total}
        resultTotal={total}
        upcomingTotal={upcomingTotal}
        recentTotal={recentTotal}
        recentSectionLabel="Past events"
        exportLabel="My Events"
        emptyMessage="You have not remembered any upcoming or recent events in this browser yet."
        telemetryCategory="remembered_events"
        remoteFiltering
        vibeOptionsOverride={vibeOptions}
        entityFilterOptions={entityOptions}
        hasMoreUpcoming={Boolean(upcomingPagination?.hasNextPage)}
        hasMoreRecent={Boolean(recentPagination?.hasNextPage)}
        isLoadingMoreUpcoming={loadingUpcoming}
        isLoadingMoreRecent={loadingRecent}
        onLoadMoreUpcoming={() => loadMore('upcoming')}
        onLoadMoreRecent={() => loadMore('recent')}
        onExportRequest={handleExport}
        toolbarExtra={recentTotal > 0 ? (
          <ExplorerControlButton
            type="button"
            onClick={handleForgetPast}
            disabled={isRemovingPast}
          >
            <BookmarkMinus aria-hidden="true" size={16} />
            {isRemovingPast ? 'Removing past events' : `Remove past (${recentTotal})`}
          </ExplorerControlButton>
        ) : undefined}
      />
    </>
  );
};

export default MyEventsPage;